import { createContext, useContext, useSyncExternalStore } from 'react';
import { useStore } from 'zustand';
import type { EditorState, EditorActions } from '../state/editor';
import { leaseAllowsEdit } from '../state/lease';
import { useBoardData } from '../collab/hooks';
import { isDocTooNew } from '../lib/schemaGate';
import { resolveUnits } from '../lib/units';
import type { Units } from '../lib/units';
import { settingsStore } from '../state/settings';
import type { EditorServices } from './services';
import type { EditTarget } from './target';

const EditorCtx = createContext<EditorServices | null>(null);

export const EditorProvider = EditorCtx.Provider;

/**
 * Access to the open editor from any component, including panes and sidebar tabs registered by
 * other features: `const { session, store, registry, getContext } = useEditor()`.
 */
export function useEditor(): EditorServices {
  const v = useContext(EditorCtx);
  if (!v) throw new Error('useEditor() outside of <EditorProvider>');
  return v;
}

/** Like useEditor(), but null outside the editor (components shown in both app header and editor). */
export function useMaybeEditor(): EditorServices | null {
  return useContext(EditorCtx);
}

export function useEditorStore<T>(selector: (s: EditorState & EditorActions) => T): T {
  return useStore(useEditor().store, selector);
}

/** Live "may I write?" (role × edit lease × board not newer than this client). */
export function useCanEdit(): boolean {
  const { lease, roleCanEdit, session } = useEditor();
  const state = useSyncExternalStore(lease.subscribe, lease.getState, lease.getState);
  const { schemaVersion } = useBoardData(session.doc).meta;
  return roleCanEdit && leaseAllowsEdit(state) && !isDocTooNew(schemaVersion);
}

/** The document being edited – the board or a discussion layer (decision U4, ./target.ts). */
export function useActiveTarget(): EditTarget {
  const { target } = useEditor();
  return useSyncExternalStore(target.subscribe, target.get, target.get);
}

/** Live "may I write to the active target?" (board: role × lease; discussion: link/overlay rights). */
export function useTargetCanEdit(): boolean {
  const target = useActiveTarget();
  const board = useCanEdit();
  return target.kind === 'board' ? board : target.canWrite();
}

/** Display units: user preference, falling back to the board's unit setting. */
export function useUnits(): Units {
  const { session } = useEditor();
  const data = useBoardData(session.doc);
  const pref = useStore(settingsStore, (s) => s.units);
  return resolveUnits(pref, data.meta.units);
}
